/**
 * The ranking arm this visit was served, remembered in the browser.
 *
 * The server decides the arm -- it holds the experiment configuration -- and
 * tells the client which one it picked in the showcase response. The client's
 * only job is to remember the label and put it on the events it emits, because
 * that label is the entire mechanism by which two arms can be told apart
 * afterwards. Without it the events from both arms are identical rows.
 *
 * `sessionStorage`, not `localStorage`, on purpose. An experiment's allocation
 * can change between visits -- an arm gets widened, an experiment is switched
 * off -- and a label that outlived the visit would mislabel every event of the
 * next one. Per-visit is exactly the right lifetime: the events of a single
 * visit are the unit the results group by, and the assignment is recomputed
 * from the stable anonymous id at the start of the next one anyway.
 *
 * Every access goes through utils/guarded-storage.ts: Safari's private mode
 * throws on the accessor itself, and an unlabelled event is a tolerable loss
 * where a crashed page is not.
 */

import { readString, writeString } from './guarded-storage';

const STORAGE_KEY = 'ranking_variant';

/** What an event carries when nothing has told us otherwise. */
export const CONTROL_VARIANT = 'control';

/**
 * Ids are used as a ClickHouse LowCardinality column, so a runaway value would
 * bloat the dictionary. The server only ever sends ids that passed the variant
 * schema; this is a second gate on a value that arrives over the wire.
 */
const VALID_ID = /^[a-z0-9][a-z0-9_-]{0,38}[a-z0-9]$/;

/** Remember the arm the server says served this response. */
export function rememberRankingVariant(variantId: string | null | undefined): void {
  if (!variantId || !VALID_ID.test(variantId)) return;
  writeString('session', STORAGE_KEY, variantId);
}

/** The arm to label this visit's events with. `control` until told otherwise. */
export function getRankingVariant(): string {
  const stored = readString('session', STORAGE_KEY);
  return stored && VALID_ID.test(stored) ? stored : CONTROL_VARIANT;
}
